import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

export interface MenuItem {
  path: string;
  icon: string;
  label: string;
  permissions?: string[];
}

export interface MenuSection {
  title: string;
  items: MenuItem[];
}

export function getMenuSections(): MenuSection[] {
  return [
    {
      title: 'FLOOR',
      items: [
        { path: '/dashboard', icon: 'fa-gauge-high', label: 'Dashboard', permissions: ['dashboard:view'] },
        { path: '/security-check', icon: 'fa-id-card', label: 'Security Check', permissions: ['gate-entry:create'] },
        { path: '/inward', icon: 'fa-truck-ramp-box', label: 'Inbound', permissions: ['grn:view', 'grn:create', 'grn:submit', 'grn:approve'] },
        { path: '/outward', icon: 'fa-truck-fast', label: 'Outbound', permissions: ['dispatch:view', 'dispatch:create', 'dispatch:submit', 'dispatch:approve'] },
        { path: '/inventory', icon: 'fa-cubes', label: 'Inventory', permissions: ['inventory:view', 'inventory-summary:view'] },
      ],
    },
    {
      title: 'COMMERCIAL',
      items: [
        { path: '/clients', icon: 'fa-building-user', label: 'Clients', permissions: ['client:update'] },
        { path: '/billing', icon: 'fa-file-invoice-dollar', label: 'Billing', permissions: ['billing:view'] },
        { path: '/billing-config', icon: 'fa-sliders', label: 'Billing Config', permissions: ['billing-config:view'] },
        { path: '/reports', icon: 'fa-chart-column', label: 'Reports', permissions: ['reports:view'] },
        { path: '/audit-report', icon: 'fa-clipboard-check', label: 'Audit Report', permissions: ['audit-report:view'] },
      ],
    },
    {
      title: 'SYSTEM',
      items: [
        { path: '/warehouse-management', icon: 'fa-sitemap', label: 'Warehouse Setup', permissions: ['warehouses:manage', 'zones:manage', 'racks:manage', 'locations:manage', 'warehouse-structure:manage'] },
        { path: '/product-catalog', icon: 'fa-box-open', label: 'Product Catalog', permissions: ['master-data:view'] },
        // Master Data module hidden — functionality retired
        // { path: '/master-data', icon: 'fa-database', label: 'Master Data', permissions: ['product-categories:manage', 'storage-requirements:manage'] },
        { path: '/two-factor-management', icon: 'fa-shield-halved', label: '2FA Management', permissions: ['two-factor:manage'] },
        { path: '/settings-users', icon: 'fa-users-gear', label: 'Users', permissions: ['users:manage'] },
        { path: '/platform-tenants', icon: 'fa-city', label: 'Tenants', permissions: ['tenants:manage', 'tenants:impersonate'] },
        { path: '/settings-roles-permissions', icon: 'fa-key', label: 'Roles & Access', permissions: ['roles:assign-permissions', 'permissions:view'] },
      ],
    },
  ];
}

export default function Sidebar() {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, hasAnyPermission } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Load collapse state from localStorage
  useEffect(() => {
    const collapsed = localStorage.getItem('sidebarCollapsed') === 'true';
    setIsCollapsed(collapsed);
  }, []);

  // Open the drawer when the mobile hamburger (in Navbar) requests it
  useEffect(() => {
    const openMobile = () => setIsMobileOpen(true);
    window.addEventListener('sidebarMobileOpen', openMobile);
    return () => window.removeEventListener('sidebarMobileOpen', openMobile);
  }, []);

  // Auto-close the mobile drawer whenever the route changes
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  // Save collapse state to localStorage
  const toggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', String(newState));
    // Dispatch custom event for same-tab updates
    window.dispatchEvent(new Event('sidebarToggle'));
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Force navigation even if logout API fails
      navigate('/login');
    } finally {
      setIsLoggingOut(false);
    }
  };

  const isActive = (path: string) => {
    if (location.pathname === path) return true;

    // Inward: GRN list, create, view, approvals
    if (path === '/inward' && (
      location.pathname.startsWith('/grn-create') ||
      location.pathname.startsWith('/grn/') ||
      location.pathname === '/grn' ||
      location.pathname === '/approvals-grn'
    )) return true;

    // Outward: dispatch create, approvals
    if (path === '/outward' && (
      location.pathname.startsWith('/dispatch-create') ||
      location.pathname === '/approvals-dispatch'
    )) return true;

    // Inventory: tabs only (exclude client inventory detail)
    if (path === '/inventory' && (
      location.pathname.startsWith('/inventory')
    )) return true;

    if (path === '/warehouse-management' && location.pathname.startsWith('/warehouse-management')) return true;
    if (path === '/billing' && location.pathname.startsWith('/billing') && !location.pathname.startsWith('/billing-config')) return true;
    if (path === '/reports' && location.pathname.startsWith('/reports')) return true;

    // Client Management: includes client list, add/edit client, and client inventory detail
    if (path === '/clients' && location.pathname.startsWith('/clients')) return true;

    return false;
  };

  const menuSections = getMenuSections();

  return (
    <>
      {isMobileOpen && (
        <div
          className="fixed inset-0 top-[58px] bg-ink-950/40 backdrop-blur-[1px] z-40 lg:hidden"
          onClick={() => setIsMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={`fixed left-0 top-[58px] lg:top-[62.5px] bottom-0 z-50 flex flex-col bg-brand-950 border-r border-brand-900 transition-all duration-250 lg:translate-x-0 ${
          isCollapsed ? 'w-[68px]' : 'w-[232px]'
        } ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        {/* Collapse control */}
        <div className={`hidden lg:flex items-center border-b border-brand-900 ${isCollapsed ? 'justify-center px-2 py-2.5' : 'justify-between px-3 py-2.5'}`}>
          {!isCollapsed && (
            <span className="text-[10px] font-extrabold tracking-[0.16em] text-brand-400 uppercase">
              Navigation
            </span>
          )}
          <button
            onClick={toggleCollapse}
            className="w-8 h-8 rounded-md border border-brand-800 text-brand-300 hover:text-accent-400 hover:border-accent-500/40 hover:bg-brand-900 flex items-center justify-center transition-colors"
            aria-label="Toggle Sidebar"
          >
            <i className={`fas ${isCollapsed ? 'fa-angles-right' : 'fa-angles-left'} text-[10px]`}></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar px-2 py-3">
          {menuSections.map((section, idx) => {
            const visibleItems = section.items.filter(
              (item) => !item.permissions || hasAnyPermission(item.permissions)
            );
            if (visibleItems.length === 0) return null;

            return (
              <div key={idx} className="mb-4">
                {!isCollapsed && (
                  <div className="text-[10px] font-extrabold text-brand-500 uppercase tracking-[0.14em] mb-1.5 px-2.5">
                    {section.title}
                  </div>
                )}
                {isCollapsed && idx > 0 && <div className="h-px bg-brand-900 mx-2 mb-2" />}
                <div className="space-y-0.5">
                  {visibleItems.map((item) => {
                    const active = isActive(item.path);
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`group flex items-center rounded-md transition-colors duration-150 ${
                          isCollapsed ? 'justify-center px-2 py-2.5' : 'px-2.5 py-2'
                        } ${
                          active
                            ? 'bg-brand-800 text-white shadow-soft'
                            : 'text-brand-200 hover:bg-brand-900 hover:text-white'
                        }`}
                        title={isCollapsed ? item.label : ''}
                      >
                        <i className={`fas ${item.icon} ${isCollapsed ? 'text-sm' : 'text-[13px] w-4 mr-2.5'} ${active ? 'text-accent-400' : 'text-brand-400 group-hover:text-accent-300'}`}></i>
                        {!isCollapsed && (
                          <span className={`text-[13px] truncate ${active ? 'font-semibold' : 'font-medium'}`}>
                            {item.label}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="p-2 border-t border-brand-900">
          <button
            onClick={handleLogout}
            disabled={isLoggingOut}
            className={`w-full rounded-md text-[13px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-brand-800 text-brand-200 hover:bg-red-500/10 hover:text-red-300 hover:border-red-400/40 ${
              isCollapsed ? 'py-2.5 px-2' : 'py-2.5 px-3 flex items-center justify-center gap-2'
            }`}
            title={isLoggingOut ? 'Logging out...' : 'Logout'}
          >
            <i className={`fas ${isLoggingOut ? 'fa-spinner fa-spin' : 'fa-right-from-bracket'} text-xs`}></i>
            {!isCollapsed && (isLoggingOut ? 'Logging out...' : 'Sign out')}
          </button>
        </div>
      </aside>
    </>
  );
}
